import { useEffect, useState } from 'react';
import { useTranslation } from '@/lib/i18n';
import { FilterBar, FilterField, Input, RadixSelect } from '@/components/ui';
import type {
  ReleaseScheduleSourceEnum,
  ReleaseScheduleStatusEnum,
} from '@/lib/api-admin/types';
import { useReleaseScheduleFiltersStore } from '@/stores/useReleaseScheduleFiltersStore';
import { VendorSelector } from '@/features/vendor/components/VendorSelector';

const DEBOUNCE_MS = 300;

const STATUS_OPTIONS: ReadonlyArray<ReleaseScheduleStatusEnum> = [
  'SCHEDULED',
  'PUBLISHED',
  'DELAYED',
  'CANCELLED',
];

const SOURCE_OPTIONS: ReadonlyArray<ReleaseScheduleSourceEnum> = ['FUNDING', 'MANUAL'];

export const ReleaseFiltersBar = () => {
  const { t } = useTranslation('settlement');
  const keyword = useReleaseScheduleFiltersStore((s) => s.keyword);
  const vendorId = useReleaseScheduleFiltersStore((s) => s.vendorId);
  const status = useReleaseScheduleFiltersStore((s) => s.status);
  const source = useReleaseScheduleFiltersStore((s) => s.source);
  const from = useReleaseScheduleFiltersStore((s) => s.from);
  const to = useReleaseScheduleFiltersStore((s) => s.to);
  const setKeyword = useReleaseScheduleFiltersStore((s) => s.setKeyword);
  const setVendorId = useReleaseScheduleFiltersStore((s) => s.setVendorId);
  const setStatuses = useReleaseScheduleFiltersStore((s) => s.setStatuses);
  const setSource = useReleaseScheduleFiltersStore((s) => s.setSource);
  const setFrom = useReleaseScheduleFiltersStore((s) => s.setFrom);
  const setTo = useReleaseScheduleFiltersStore((s) => s.setTo);

  const [localKeyword, setLocalKeyword] = useState(keyword);

  useEffect(() => {
    if (localKeyword === keyword) return;
    const id = setTimeout(() => setKeyword(localKeyword), DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [localKeyword, keyword, setKeyword]);

  const singleStatus: '' | ReleaseScheduleStatusEnum =
    status.length === 1 ? (status[0] as ReleaseScheduleStatusEnum) : '';

  const statusOptions = [
    { value: '', label: t('settlement.release.filters.statusAll') },
    ...STATUS_OPTIONS.map((opt) => ({ value: opt, label: t(`settlement.releaseStatus.${opt}`) })),
  ];
  const sourceOptions = [
    { value: '', label: t('settlement.release.filters.sourceAll') },
    ...SOURCE_OPTIONS.map((opt) => ({ value: opt, label: t(`settlement.release.source.${opt}`) })),
  ];

  return (
    <FilterBar
      aria-label={t('settlement.release.filters.label')}
      data-testid="release-filters"
      filters={
        <>
          <FilterField label={t('settlement.release.filters.keyword')}>
            <Input
              id="release-keyword"
              value={localKeyword}
              onChange={(e) => setLocalKeyword(e.target.value)}
              placeholder={t('settlement.release.filters.keywordPlaceholder')}
              className="w-full"
              maxLength={100}
            />
          </FilterField>
          <FilterField label={t('settlement.release.filters.vendorId')}>
            <VendorSelector
              value={vendorId ?? null}
              onChange={setVendorId}
              placeholder={t('settlement.release.filters.vendorId')}
            />
          </FilterField>
          <FilterField label={t('settlement.release.filters.status')}>
            <RadixSelect
              id="release-status"
              value={singleStatus}
              onChange={(v) => setStatuses(v === '' ? [] : [v as ReleaseScheduleStatusEnum])}
              options={statusOptions}
              className="w-full"
            />
          </FilterField>
          <FilterField label={t('settlement.release.filters.source')}>
            <RadixSelect
              id="release-source"
              value={source ?? ''}
              onChange={(v) => setSource(v === '' ? null : (v as ReleaseScheduleSourceEnum))}
              options={sourceOptions}
              className="w-full"
            />
          </FilterField>
          <FilterField label={t('settlement.release.filters.from')}>
            <Input
              id="release-from"
              type="datetime-local"
              value={from ? from.slice(0, 16) : ''}
              onChange={(e) => setFrom(e.target.value === '' ? null : new Date(e.target.value).toISOString())}
              className="w-full"
            />
          </FilterField>
          <FilterField label={t('settlement.release.filters.to')}>
            <Input
              id="release-to"
              type="datetime-local"
              value={to ? to.slice(0, 16) : ''}
              onChange={(e) => setTo(e.target.value === '' ? null : new Date(e.target.value).toISOString())}
              className="w-full"
            />
          </FilterField>
        </>
      }
    />
  );
};
